// Bulk "Envoyer à l'imprimeur" — order selection only.
//
// Link creation is now INSTANT: the admin browser does NO downloading/zipping.
// It just records which selected orders are print-ready; the link stores the
// order ids and the public page (PrinterLinkPage) builds each order's ZIP on
// demand, in the PRINTER's browser, when they click it. Orders whose PDFs aren't
// ready yet are skipped + flagged (no in-browser regeneration at scale).
import type { Order } from '@/lib/api';
import { paidForPrint } from '@/lib/paidState';

export interface SendToPrinterResult {
  /** Human order numbers included (ready). */
  orderIds: string[];
  /** Shopify order ids included — the printer-link payload. */
  shopifyOrderIds: string[];
  /** Orders left out because their PDF isn't ready OR not paid, with the reason. */
  skipped: Array<{ orderName: string; reason: string }>;
}

// Payment gate (2026-06-21): no printing without payment received. Mirror of the
// worker's check (isPaidOrder): money in however it was recorded — the one
// definition, lib/paidState.ts (2026-10-06: an accepted advance with no paid_at,
// #1184, was held here) — OR status at/after 'paid' in the pipeline.
function isPaidOrder(o: Order): boolean {
  return paidForPrint(o);
}

/** Split the selected orders into ready (included) vs not-ready (skipped).
 *  Synchronous + instant — no network, no zipping. An order is sendable only
 *  when its PDF is ready AND it has been paid. */
export function selectReadyOrders(orders: Order[]): SendToPrinterResult {
  const orderIds: string[] = [];
  const shopifyOrderIds: string[] = [];
  const skipped: Array<{ orderName: string; reason: string }> = [];
  for (const o of orders) {
    const name = o.shopify_order_name || o.shopify_order_id;
    if (o.pdf_status !== 'ready') {
      skipped.push({ orderName: name, reason: 'PDF pas prêt — régénérez-le d’abord' });
      continue;
    }
    if (!isPaidOrder(o)) {
      skipped.push({ orderName: name, reason: 'Non payée — marquez-la Payée d’abord' });
      continue;
    }
    orderIds.push(name);
    shopifyOrderIds.push(o.shopify_order_id);
  }
  return { orderIds, shopifyOrderIds, skipped };
}
